import React from 'react';
import { Button, Input } from '@arco-design/web-react';
import { Importance, MessageDetail, ReviewFields } from './types';
import {
  FieldIssue,
  codePointLength,
  groupMissingCount,
  importanceLabels
} from './reviewUiState';

const importanceOptions: Importance[] = ['GENERAL', 'IMPORTANT', 'VERY_IMPORTANT'];

function ListEditor({
  label,
  items,
  onChange,
  disabled,
  issues
}: {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  disabled: boolean;
  issues: FieldIssue[];
}) {
  const update = (index: number, value: string) => onChange(items.map((item, i) => (i === index ? value : item)));
  const move = (index: number, offset: number) => {
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(index + offset, 0, item);
    onChange(next);
  };
  return (
    <div className="message-list-editor">
      {items.map((item, index) => {
        const issue = issues.find(entry => entry.itemIndex === index);
        return (
          <div key={index} className="message-list-row">
            <Input
              id={issue ? `review-field-${issue.field}-${index}` : undefined}
              aria-label={`${label}第${index + 1}项`}
              disabled={disabled}
              value={item}
              onChange={value => update(index, value)}
            />
            <span className="message-char-count">{codePointLength(item)} / 500</span>
            <Button aria-label={`上移${label}第${index + 1}项`} disabled={disabled || index === 0} onClick={() => move(index, -1)}>
              上移
            </Button>
            <Button
              aria-label={`下移${label}第${index + 1}项`}
              disabled={disabled || index === items.length - 1}
              onClick={() => move(index, 1)}
            >
              下移
            </Button>
            <Button aria-label={`删除${label}第${index + 1}项`} disabled={disabled} onClick={() => onChange(items.filter((_, i) => i !== index))}>
              删除
            </Button>
            {issue && <p className="message-field-error">{issue.message}</p>}
          </div>
        );
      })}
      <Button aria-label={`添加${label}条目`} disabled={disabled || items.length >= 8} onClick={() => onChange([...items, ''])}>
        添加一项
      </Button>
      <p className="message-char-count">{items.length} / 8 项</p>
    </div>
  );
}

function FieldError({ issues, field }: { issues: FieldIssue[]; field: string }) {
  const issue = issues.find(item => item.field === field && item.itemIndex == null);
  if (!issue) return null;
  return (
    <p className="message-field-error" id={`review-error-${field}`}>
      {issue.message}
    </p>
  );
}

function CharCount({ value, max }: { value: string; max: number }) {
  return (
    <span className="message-char-count">
      {codePointLength(value)} / {max}
    </span>
  );
}

export default function MessageReviewForm({
  detail,
  fields,
  locked,
  analysisV2,
  seedSource,
  showErrors,
  issues,
  onChange
}: {
  detail: MessageDetail;
  fields: ReviewFields;
  locked: boolean;
  analysisV2: boolean;
  seedSource: 'review' | 'analysis' | 'empty';
  showErrors: boolean;
  issues: FieldIssue[];
  onChange: <K extends keyof ReviewFields>(name: K, value: ReviewFields[K]) => void;
}) {
  const visibleIssues = showErrors ? issues : [];
  const publication = detail.publication;
  const showSeedWarning =
    seedSource === 'analysis' &&
    publication?.visible &&
    (fields.importance !== publication.importance || fields.featured !== publication.featured);
  const groups: { id: string; label: string; group: 'basic' | 'facts' | 'impact' | 'ai' }[] = [
    { id: 'review-group-basic', label: '基本信息', group: 'basic' },
    { id: 'review-group-facts', label: '关键事实', group: 'facts' },
    { id: 'review-group-impact', label: '影响与行动', group: 'impact' },
    { id: 'review-group-ai', label: 'AI解读', group: 'ai' }
  ];
  return (
    <div className="message-review">
      <p>
        内容来源：{seedSource === 'review' ? '已保存审核' : seedSource === 'analysis' ? '当前 AI 分析' : '尚未填写'}
        。这里修改的是人工审核版本；保存后不会自动发布。
      </p>
      {showSeedWarning && (
        <p role="status" className="message-seed-warning">
          当前以 AI 建议初始化，重要度/精选可能与公开版本不同。草稿：
          {importanceLabels[fields.importance]} / {fields.featured ? '精选' : '未精选'}；公开版本：
          {importanceLabels[publication.importance]} / {publication.featured ? '精选' : '未精选'}。
        </p>
      )}
      <nav className="message-group-nav" aria-label="审核内容分组">
        {groups.map(item => {
          const missing = groupMissingCount(visibleIssues, item.group);
          return (
            <Button
              key={item.id}
              type="text"
              onClick={() => document.getElementById(item.id)?.scrollIntoView({ block: 'start' })}
            >
              {item.label}
              {missing ? ` · ${missing}` : ''}
            </Button>
          );
        })}
      </nav>
      <section id="review-group-basic" className="message-review-group" aria-label="基本信息">
        <h3>基本信息</h3>
        <label>
          审核标题
          <Input
            id="review-field-title"
            aria-label="审核标题"
            disabled={locked}
            value={fields.title}
            onChange={value => onChange('title', value)}
          />
          <CharCount value={fields.title} max={500} />
          <FieldError issues={visibleIssues} field="title" />
        </label>
        {analysisV2 && (
          <>
            <label>
              主题
              <Input
                id="review-field-topic"
                aria-label="审核主题"
                disabled={locked}
                value={fields.topic}
                onChange={value => onChange('topic', value)}
              />
              <CharCount value={fields.topic} max={64} />
              <FieldError issues={visibleIssues} field="topic" />
            </label>
          </>
        )}
        <label>
          重要程度
          <select
            id="review-field-importance"
            aria-label="审核重要程度"
            disabled={locked}
            value={fields.importance}
            onChange={event => onChange('importance', event.target.value as Importance)}
          >
            {importanceOptions.map(value => (
              <option key={value} value={value}>
                {importanceLabels[value]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            aria-label="精选快讯"
            disabled={locked}
            checked={fields.featured}
            onChange={event => onChange('featured', event.target.checked)}
          />{' '}
          精选
        </label>
        <label>
          摘要
          <Input.TextArea
            id="review-field-summary"
            aria-label="审核摘要"
            disabled={locked}
            value={fields.summary}
            onChange={value => onChange('summary', value)}
            autoSize={{ minRows: 3, maxRows: 8 }}
          />
          <CharCount value={fields.summary} max={8000} />
          <FieldError issues={visibleIssues} field="summary" />
        </label>
        <label>
          审核解读
          <span className="message-field-hint">整体解读正文</span>
          <Input.TextArea
            id="review-field-interpretation"
            aria-label="审核解读"
            disabled={locked}
            value={fields.interpretation}
            onChange={value => onChange('interpretation', value)}
            autoSize={{ minRows: 4, maxRows: 8 }}
          />
          <CharCount value={fields.interpretation} max={16000} />
          <FieldError issues={visibleIssues} field="interpretation" />
        </label>
      </section>
      {!analysisV2 ? (
        <p className="message-structured-unavailable">当前分析缺少结构化字段，不能保存审核。</p>
      ) : (
        <>
          <section id="review-group-facts" className="message-review-group" aria-label="关键事实">
            <h3>关键事实</h3>
            <ListEditor
              label="关键事实"
              items={fields.keyFacts}
              disabled={locked}
              issues={visibleIssues.filter(item => item.field === 'keyFacts')}
              onChange={items => onChange('keyFacts', items)}
            />
            <FieldError issues={visibleIssues} field="keyFacts" />
          </section>
          <section id="review-group-impact" className="message-review-group" aria-label="影响与行动">
            <h3>影响与行动</h3>
            <label>
              短期影响
              <Input.TextArea
                id="review-field-shortTermImpact"
                aria-label="审核短期影响"
                disabled={locked}
                value={fields.shortTermImpact}
                onChange={value => onChange('shortTermImpact', value)}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
              <CharCount value={fields.shortTermImpact} max={2000} />
              <FieldError issues={visibleIssues} field="shortTermImpact" />
            </label>
            <label>
              中期影响
              <Input.TextArea
                id="review-field-mediumTermImpact"
                aria-label="审核中期影响"
                disabled={locked}
                value={fields.mediumTermImpact}
                onChange={value => onChange('mediumTermImpact', value)}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
              <CharCount value={fields.mediumTermImpact} max={2000} />
              <FieldError issues={visibleIssues} field="mediumTermImpact" />
            </label>
            <label>
              行动指引
              <Input.TextArea
                id="review-field-actionGuidance"
                aria-label="审核行动含义"
                disabled={locked}
                value={fields.actionGuidance}
                onChange={value => onChange('actionGuidance', value)}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
              <CharCount value={fields.actionGuidance} max={2000} />
              <FieldError issues={visibleIssues} field="actionGuidance" />
            </label>
          </section>
          <section id="review-group-ai" className="message-review-group" aria-label="AI解读">
            <h3>AI解读</h3>
            <p className="message-field-hint">详情页各分项内容</p>
            <label>
              事件还原
              <Input.TextArea
                id="review-field-eventReconstruction"
                aria-label="审核事件还原"
                disabled={locked}
                value={fields.eventReconstruction}
                onChange={value => onChange('eventReconstruction', value)}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
              <CharCount value={fields.eventReconstruction} max={4000} />
              <FieldError issues={visibleIssues} field="eventReconstruction" />
            </label>
            <label>
              可信度说明
              <Input.TextArea
                id="review-field-confidenceAnalysis"
                aria-label="审核可信度说明"
                disabled={locked}
                value={fields.confidenceAnalysis}
                onChange={value => onChange('confidenceAnalysis', value)}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
              <CharCount value={fields.confidenceAnalysis} max={2000} />
              <FieldError issues={visibleIssues} field="confidenceAnalysis" />
            </label>
            <fieldset>
              <legend>传导路径</legend>
              <ListEditor
                label="传导路径"
                items={fields.transmissionPath}
                disabled={locked}
                issues={visibleIssues.filter(item => item.field === 'transmissionPath')}
                onChange={items => onChange('transmissionPath', items)}
              />
              <FieldError issues={visibleIssues} field="transmissionPath" />
            </fieldset>
            <label>
              预期差
              <Input.TextArea
                id="review-field-expectationGap"
                aria-label="审核预期差"
                disabled={locked}
                value={fields.expectationGap}
                onChange={value => onChange('expectationGap', value)}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
              <CharCount value={fields.expectationGap} max={2000} />
              <FieldError issues={visibleIssues} field="expectationGap" />
            </label>
            <fieldset>
              <legend>行动清单</legend>
              <ListEditor
                label="行动清单"
                items={fields.actionItems}
                disabled={locked}
                issues={visibleIssues.filter(item => item.field === 'actionItems')}
                onChange={items => onChange('actionItems', items)}
              />
              <FieldError issues={visibleIssues} field="actionItems" />
            </fieldset>
          </section>
        </>
      )}
    </div>
  );
}
